import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  CreateFlightOfferInput,
  FlightListQuery,
  FlightOfferDto,
  UpdateFlightOfferInput,
} from '@wingseat/shared';
import { api } from '../lib/api-client';

export const flightKeys = {
  all: ['flights'] as const,
  list: (query?: Partial<FlightListQuery>) =>
    ['flights', 'list', query ?? {}] as const,
  detail: (id: string) => ['flights', 'detail', id] as const,
};

function toQueryRecord(
  q: Partial<FlightListQuery>,
): Record<string, string | number | boolean | undefined | null> {
  return {
    aircraftTypeId: q.aircraftTypeId,
    fromDate: q.fromDate,
    toDate: q.toDate,
    fromLocation: q.fromLocation,
    onlyAvailable: q.onlyAvailable,
    status: q.status,
  };
}

export function useFlights(
  query: Partial<FlightListQuery> = {},
  options?: Omit<UseQueryOptions<FlightOfferDto[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FlightOfferDto[]>({
    queryKey: flightKeys.list(query),
    queryFn: () =>
      api.get<FlightOfferDto[]>('/flights', { query: toQueryRecord(query) }),
    ...options,
  });
}

export function useFlight(id: string | undefined) {
  return useQuery({
    queryKey: id ? flightKeys.detail(id) : ['flights', 'detail', 'idle'],
    queryFn: () => api.get<FlightOfferDto>(`/flights/${id!}`),
    enabled: !!id,
  });
}

export function useCreateFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFlightOfferInput) =>
      api.post<FlightOfferDto>('/flights', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flightKeys.all });
    },
  });
}

export function useUpdateFlight(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateFlightOfferInput) =>
      api.patch<FlightOfferDto>(`/flights/${id}`, input),
    onSuccess: (data) => {
      qc.setQueryData(flightKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: flightKeys.all });
    },
  });
}

export function useCancelFlight(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | null) =>
      api.post<FlightOfferDto>(`/flights/${id}/cancel`, { reason }),
    onSuccess: (data) => {
      qc.setQueryData(flightKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: flightKeys.all });
    },
  });
}

export function useRequestBooking(flightId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string | null) =>
      api.post(`/flights/${flightId}/request-booking`, { message }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flightKeys.detail(flightId) });
      qc.invalidateQueries({ queryKey: ['booking-requests'] });
    },
  });
}
